import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Car } from 'lucide-react';

/** client#11: vehicle use recorded against a shift (trip logs, `transport_logs.shift_id`), shown on timesheets. */

const KIND = { personal: 'Personal vehicle', company: 'Company vehicle', participant: "Participant's vehicle" };
const STATUS_TONE = {
  APPROVED: 'bg-green-600 text-white', AUTO_APPROVED: 'bg-green-600 text-white',
  SUBMITTED: 'bg-yellow-500 text-black', REJECTED: 'bg-red-600 text-white',
};

export const tripKm = (t) => Number(t?.distance_km || 0);
export const totalKm = (trips) => Math.round((trips || []).reduce((s, t) => s + tripKm(t), 0) * 10) / 10;
const vehicleName = (t) => t.vehicle_label || KIND[t.vehicle_kind] || 'Vehicle';
const statusLabel = (s) => (s || '').replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());

/** Trip logs grouped by shift id — one request for a whole timesheet list. Empty when transport is off / not permitted. */
export function useTripLogsByShift(enabled = true, params = undefined) {
  const [byShift, setByShift] = useState({});
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    api.get('/transport/logs', { params })
      .then((r) => {
        const map = {};
        for (const t of (r.data?.data || [])) if (t.shift_id) (map[t.shift_id] = map[t.shift_id] || []).push(t);
        if (alive) setByShift(map);
      })
      .catch(() => { if (alive) setByShift({}); });
    return () => { alive = false; };
  }, [enabled, JSON.stringify(params || {})]);   // eslint-disable-line react-hooks/exhaustive-deps
  return byShift;
}

/** Compact cell for a timesheet table row. */
export function TripLogSummary({ trips, testId }) {
  if (!trips || !trips.length) return <span className="text-muted-foreground text-xs">—</span>;
  const names = [...new Set(trips.map(vehicleName))].join(', ');
  return (
    <div className="text-xs" data-testid={testId} title={trips.map(t => `${vehicleName(t)}: ${tripKm(t)} km`).join('\n')}>
      <p className="font-medium flex items-center gap-1"><Car className="h-3 w-3" />{totalKm(trips)} km</p>
      <p className="text-muted-foreground truncate max-w-[160px]">{names}{trips.length > 1 ? ` · ${trips.length} trips` : ''}</p>
    </div>
  );
}

/** Full section for a timesheet detail view; loads the trip logs of one shift. */
export function ShiftVehicleLog({ shiftId }) {
  const [state, setState] = useState({ loading: true, trips: [], unavailable: false });
  useEffect(() => {
    if (!shiftId) { setState({ loading: false, trips: [], unavailable: false }); return undefined; }
    let alive = true;
    setState(s => ({ ...s, loading: true }));
    api.get('/transport/logs', { params: { shift_id: shiftId } })
      .then(r => { if (alive) setState({ loading: false, trips: r.data?.data || [], unavailable: false }); })
      .catch(e => { if (alive) setState({ loading: false, trips: [], unavailable: [403, 404].includes(e?.response?.status) }); });
    return () => { alive = false; };
  }, [shiftId]);

  if (state.loading) return <p className="text-xs text-muted-foreground">Loading vehicle log…</p>;
  if (state.unavailable) return <p className="text-xs text-muted-foreground" data-testid="vehicle-log-unavailable">Vehicle log not available (transport logging is off or not permitted).</p>;
  if (!state.trips.length) return <p className="text-xs text-muted-foreground" data-testid="vehicle-log-empty">No vehicle use recorded for this shift.</p>;
  return (
    <div className="space-y-2" data-testid="vehicle-log">
      {state.trips.map(t => (
        <div key={t.id} className="rounded border p-2 text-xs space-y-1" data-testid={`vehicle-log-trip-${t.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium flex items-center gap-1"><Car className="h-3.5 w-3.5" />{vehicleName(t)}</span>
            {t.status && <Badge className={`text-[10px] ${STATUS_TONE[t.status] || 'bg-slate-200 text-slate-800'}`}>{statusLabel(t.status)}</Badge>}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-muted-foreground">
            <span>Distance: <span className="text-foreground font-mono">{tripKm(t)} km</span></span>
            {(t.start_odometer != null || t.end_odometer != null) && (
              <span>Odometer: <span className="text-foreground font-mono">{t.start_odometer ?? '–'} → {t.end_odometer ?? '–'}</span></span>
            )}
            {(t.start_time || t.end_time) && <span>Time: <span className="text-foreground font-mono">{t.start_time || '–'} – {t.end_time || '–'}</span></span>}
            {t.purpose && <span>Purpose: <span className="text-foreground">{t.purpose.replace(/_/g, ' ')}</span></span>}
            {(t.start_location || t.end_location) && (
              <span className="col-span-2">Route: <span className="text-foreground">{t.start_location || '–'} → {t.end_location || '–'}</span></span>
            )}
          </div>
          {t.notes && <p className="italic text-muted-foreground">{t.notes}</p>}
          {t.has_exceptions && <p className="text-amber-700">{(t.exceptions || []).map(x => x.message).join(' · ')}</p>}
        </div>
      ))}
      {state.trips.length > 1 && <p className="text-xs font-medium" data-testid="vehicle-log-total">Total: {totalKm(state.trips)} km</p>}
    </div>
  );
}
